import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Box,
  Paper,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Divider,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  InputAdornment,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import {
  getShopifyProduct,
  updateShopifyProduct,
} from '../services/shopifyAPIService';

const EditShopifyProductPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { productId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [originalProduct, setOriginalProduct] = useState(null);

  // Basic Product Info
  const [productData, setProductData] = useState({
    title: '',
    bodyHtml: '',
    vendor: '',
    productType: '',
    status: 'draft',
    tags: '',
    handle: '',
    seoTitle: '',
    seoDescription: '',
  });

  // Variants
  const [variants, setVariants] = useState([]);

  // Product Options
  const [options, setOptions] = useState([]);

  // Images - Handle both existing (URL) and new (File) images
  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);

  const [errors, setErrors] = useState({});
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const steps = ['Basic Info', 'Variants & Pricing', 'Images & SEO', 'Review & Save'];

  useEffect(() => {
    if (productId) {
      fetchProductDetails();
    }
  }, [productId]);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching product details for editing:', productId);

      const response = await getShopifyProduct(productId);
      console.log('📦 Product data:', response);

      let productData = null;

      // ✅ Handle backend response structure
      if (response?.IsSuccess && response?.Data) {
        productData = response.Data;
      } else if (response?.payload) {
        productData = response.payload;
      } else if (response?.data) {
        productData = response.data;
      } else {
        productData = response;
      }

      setOriginalProduct(productData);

      // Set basic product info
      setProductData({
        title: productData.Title || '',
        bodyHtml: productData.BodyHtml || '',
        vendor: productData.Vendor || '',
        productType: productData.ProductType || '',
        status: productData.Status || 'draft',
        tags: productData.Tags || '',
        handle: productData.Handle || '',
        seoTitle: productData.SeoTitle || '',
        seoDescription: productData.SeoDescription || '',
      });

      // Set variants
      setVariants(
        (productData.Variants || []).map((v) => ({
          id: v.Id,
          title: v.Title || '',
          price: v.Price || 0,
          compareAtPrice: v.CompareAtPrice || null,
          sku: v.Sku || '',
          barcode: v.Barcode || '',
          inventoryQuantity: v.InventoryQuantity || 0,
          inventoryPolicy: v.InventoryPolicy || 'deny',
          inventoryManagement: v.InventoryManagement || 'shopify',
          fulfillmentService: v.FulfillmentService || 'manual',
          weight: v.Weight || null,
          weightUnit: v.WeightUnit || 'kg',
          requiresShipping: v.RequiresShipping !== false,
          taxable: v.Taxable !== false,
          option1: v.Option1 || null,
          option2: v.Option2 || null,
          option3: v.Option3 || null,
          position: v.Position || 1,
          inventoryItemId: v.InventoryItemId,
        }))
      );

      // Set options
      setOptions(
        (productData.Options || []).map((opt) => ({
          id: opt.Id,
          name: opt.Name || '',
          values: opt.Values || [],
          position: opt.Position || 1,
        }))
      );

      // Set existing images
      setExistingImages(
        (productData.Images || []).map((img) => ({
          id: img.Id,
          src: img.Src,
          position: img.Position || 1,
          variantIds: img.VariantIds || [],
        }))
      );

      console.log('✅ Product loaded for editing');
      toast.success('Product loaded successfully');
    } catch (error) {
      console.error('❌ Error fetching product:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to load product';
      toast.error(errorMessage);
      navigate('/shopify/products');
    } finally {
      setLoading(false);
    }
  };

  // Handle product data change
  const handleProductChange = (field, value) => {
    setProductData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Auto-generate handle from title only if handle is empty or matches old title
    if (field === 'title' && originalProduct) {
      const oldHandle = originalProduct.Title?.toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      
      if (!productData.handle || productData.handle === oldHandle) {
        const newHandle = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '');
        setProductData((prev) => ({
          ...prev,
          handle: newHandle,
        }));
      }
    }
  };

  // Handle variant change
  const handleVariantChange = (index, field, value) => {
    const updatedVariants = [...variants];
    updatedVariants[index] = {
      ...updatedVariants[index],
      [field]: value,
    };
    setVariants(updatedVariants);
  };

  // Add new variant
  const handleAddVariant = () => {
    setVariants([
      ...variants,
      {
        id: null, // null means it's a new variant
        title: `Variant ${variants.length + 1}`,
        price: 0,
        compareAtPrice: null,
        sku: '',
        barcode: '',
        inventoryQuantity: 0,
        inventoryPolicy: 'deny',
        inventoryManagement: 'shopify',
        fulfillmentService: 'manual',
        weight: null,
        weightUnit: 'kg',
        requiresShipping: true,
        taxable: true,
        option1: null,
        option2: null,
        option3: null,
        position: variants.length + 1,
      },
    ]);
    toast.info('New variant added');
  };

  // Remove variant
  const handleRemoveVariant = (index) => {
    if (variants.length > 1) {
      const variant = variants[index];
      if (variant.id) {
        toast.warning('Existing variants will be removed on save');
      }
      setVariants(variants.filter((_, i) => i !== index));
    } else {
      toast.warning('At least one variant is required');
    }
  };

  // Handle option change
  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...options];
    updatedOptions[index] = {
      ...updatedOptions[index],
      [field]: value,
    };
    setOptions(updatedOptions);
  };

  // Add new option
  const handleAddOption = () => {
    if (options.length < 3) {
      setOptions([
        ...options,
        {
          id: null,
          name: '',
          values: [''],
          position: options.length + 1,
        },
      ]);
      toast.info('New option added');
    } else {
      toast.warning('Maximum 3 options allowed');
    }
  };

  // Remove option
  const handleRemoveOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
    toast.info('Option removed');
  };

  // Add option value
  const handleAddOptionValue = (optionIndex) => {
    const updatedOptions = [...options];
    updatedOptions[optionIndex].values.push('');
    setOptions(updatedOptions);
  };

  // Update option value
  const handleUpdateOptionValue = (optionIndex, valueIndex, value) => {
    const updatedOptions = [...options];
    updatedOptions[optionIndex].values[valueIndex] = value;
    setOptions(updatedOptions);
  };

  // Remove option value
  const handleRemoveOptionValue = (optionIndex, valueIndex) => {
    const updatedOptions = [...options];
    if (updatedOptions[optionIndex].values.length > 1) {
      updatedOptions[optionIndex].values.splice(valueIndex, 1);
      setOptions(updatedOptions);
    }
  };

  // ✅ Convert file to base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // ✅ Handle new image file selection
  const handleImageFileChange = async (event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    // Validate file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = files.filter((file) => !validTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      toast.error('Please upload only image files (JPEG, PNG, GIF, WEBP)');
      return;
    }

    // Validate file sizes (max 5MB per image)
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = files.filter((file) => file.size > maxSize);

    if (oversizedFiles.length > 0) {
      toast.error('Some images are too large. Maximum size is 5MB per image.');
      return;
    }

    try {
      setSaving(true);

      // Convert all files to base64
      const imagePromises = files.map(async (file) => {
        const base64 = await convertFileToBase64(file);
        const preview = URL.createObjectURL(file);

        return {
          file,
          base64,
          preview,
          name: file.name,
          size: file.size,
        };
      });

      const uploadedImages = await Promise.all(imagePromises);

      setNewImages((prev) => [...prev, ...uploadedImages]);
      toast.success(`${uploadedImages.length} image(s) uploaded successfully`);
    } catch (error) {
      console.error('Error converting images:', error);
      toast.error('Failed to upload images');
    } finally {
      setSaving(false);
    }
  };

  // ✅ Remove existing image
  const handleRemoveExistingImage = (imageId) => {
    setDeletedImageIds((prev) => [...prev, imageId]);
    setExistingImages((prev) => prev.filter((img) => img.id !== imageId));
    toast.info('Image marked for deletion');
  };

  // ✅ Remove new image
  const handleRemoveNewImage = (index) => {
    setNewImages((prev) => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
    toast.info('Image removed');
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!productData.title.trim()) {
      newErrors.title = 'Product title is required';
    }

    if (variants.some((v) => v.price <= 0)) {
      newErrors.price = 'All variants must have a valid price';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setConfirmDialogOpen(true);
  };

  const handleConfirmSave = async () => {
    setConfirmDialogOpen(false);

    try {
      setSaving(true);

      // Clean up variants
      const cleanedVariants = variants.map((v) => ({
        id: v.id || null,
        title: v.title,
        price: v.price,
        compareAtPrice: v.compareAtPrice || null,
        sku: v.sku || '',
        barcode: v.barcode || '',
        inventoryQuantity: v.inventoryQuantity,
        inventoryPolicy: v.inventoryPolicy,
        inventoryManagement: v.inventoryManagement,
        fulfillmentService: v.fulfillmentService,
        weight: v.weight || null,
        weightUnit: v.weightUnit,
        requiresShipping: v.requiresShipping,
        taxable: v.taxable,
        option1: v.option1 || null,
        option2: v.option2 || null,
        option3: v.option3 || null,
        position: v.position,
        inventoryItemId: v.inventoryItemId,
      }));

      // Clean up options
      const cleanedOptions = options
        .filter((opt) => opt.name.trim() && opt.values.some((v) => v.trim()))
        .map((opt) => ({
          id: opt.id || null,
          name: opt.name,
          values: opt.values.filter((v) => v.trim()),
          position: opt.position,
        }));

      // Extract base64 strings from new images
      const newImageFiles = newImages.map((img) => img.base64);

      const payload = {
        id: productId,
        ...productData,
        variants: cleanedVariants,
        options: cleanedOptions,
        existingImageIds: existingImages.map((img) => img.id),
        deletedImageIds: deletedImageIds,
        newImageFiles: newImageFiles,
      };

      console.log('💾 Updating Shopify product:', {
        ...payload,
        newImageFiles: `${newImageFiles.length} new images`,
      });

      const response = await updateShopifyProduct(productId, payload);

      console.log('✅ Product updated:', response);

      toast.success('Product updated successfully in Shopify!');
      navigate(`/shopify/products/view/${productId}`);
    } catch (error) {
      console.error('❌ Error updating product:', error);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to update product';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      navigate(`/shopify/products/view/${productId}`);
    }
  };

  // Handle step navigation
  const handleNext = () => {
    if (activeStep === 0 && !productData.title.trim()) {
      toast.warning('Product title is required');
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  // Render step content
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderBasicInfo();
      case 1:
        return renderVariantsAndPricing();
      case 2:
        return renderImagesAndSEO();
      case 3:
        return renderReview();
      default:
        return null;
    }
  };

  // Step 1: Basic Info
  const renderBasicInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label="Product Title"
          value={productData.title}
          onChange={(e) => handleProductChange('title', e.target.value)}
          error={!!errors.title}
          helperText={errors.title}
          placeholder="e.g., Classic T-Shirt"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Description (HTML)"
          value={productData.bodyHtml}
          onChange={(e) => handleProductChange('bodyHtml', e.target.value)}
          placeholder="Enter product description (HTML supported)"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Vendor"
          value={productData.vendor}
          onChange={(e) => handleProductChange('vendor', e.target.value)}
          placeholder="e.g., Nike, Adidas"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Product Type"
          value={productData.productType}
          onChange={(e) => handleProductChange('productType', e.target.value)}
          placeholder="e.g., Clothing, Electronics"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={productData.status}
            label="Status"
            onChange={(e) => handleProductChange('status', e.target.value)}
          >
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Tags"
          value={productData.tags}
          onChange={(e) => handleProductChange('tags', e.target.value)}
          placeholder="e.g., summer, sale, new"
          helperText="Comma-separated tags"
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="URL Handle"
          value={productData.handle}
          onChange={(e) => handleProductChange('handle', e.target.value)}
          placeholder="product-url-handle"
          helperText="This will be used in the product URL"
        />
      </Grid>
    </Grid>
  );

  // Step 2: Variants and Pricing
  const renderVariantsAndPricing = () => (
    <Box>
      {/* Product Options */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Product Options</Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddOption}
            disabled={options.length >= 3}
            size="small"
          >
            Add Option
          </Button>
        </Box>

        {options.length > 0 ? (
          options.map((option, optIndex) => (
            <Card key={optIndex} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <TextField
                    label="Option Name"
                    value={option.name}
                    onChange={(e) => handleOptionChange(optIndex, 'name', e.target.value)}
                    placeholder="e.g., Size, Color"
                    sx={{ flex: 1, mr: 2 }}
                  />
                  <IconButton color="error" onClick={() => handleRemoveOption(optIndex)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Option Values
                </Typography>

                {option.values.map((value, valIndex) => (
                  <Box key={valIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={value}
                      onChange={(e) => handleUpdateOptionValue(optIndex, valIndex, e.target.value)}
                      placeholder="e.g., Small, Red"
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveOptionValue(optIndex, valIndex)}
                      disabled={option.values.length === 1}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}

                <Button size="small" startIcon={<AddIcon />} onClick={() => handleAddOptionValue(optIndex)}>
                  Add Value
                </Button>
              </CardContent>
            </Card>
          ))
        ) : (
          <Alert severity="info">No options defined. Options help organize variants (e.g., Size, Color).</Alert>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Variants */}
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Variants ({variants.length})
            {variants.some((v) => !v.id) && (
              <Chip label="Has New" size="small" color="success" sx={{ ml: 1 }} />
            )}
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddVariant} size="small">
            Add Variant
          </Button>
        </Box>

        {variants.map((variant, index) => (
          <Card key={index} sx={{ mb: 2, border: variant.id ? 'none' : '2px solid', borderColor: 'success.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {variant.id ? `Variant ${index + 1}` : `New Variant ${index + 1}`}
                  </Typography>
                  {!variant.id && <Chip label="NEW" size="small" color="success" />}
                  {variant.id && (
                    <Typography variant="caption" color="text.secondary">
                      (ID: {variant.id})
                    </Typography>
                  )}
                </Box>
                <IconButton color="error" onClick={() => handleRemoveVariant(index)} disabled={variants.length === 1}>
                  <DeleteIcon />
                </IconButton>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Variant Title"
                    value={variant.title}
                    onChange={(e) => handleVariantChange(index, 'title', e.target.value)}
                    placeholder="e.g., Small / Red"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Price"
                    value={variant.price}
                    onChange={(e) => handleVariantChange(index, 'price', parseFloat(e.target.value) || 0)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    error={errors.price && variant.price <= 0}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Compare At Price"
                    value={variant.compareAtPrice || ''}
                    onChange={(e) =>
                      handleVariantChange(index, 'compareAtPrice', e.target.value ? parseFloat(e.target.value) : null)
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="SKU"
                    value={variant.sku}
                    onChange={(e) => handleVariantChange(index, 'sku', e.target.value)}
                    placeholder="Stock Keeping Unit"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Barcode"
                    value={variant.barcode}
                    onChange={(e) => handleVariantChange(index, 'barcode', e.target.value)}
                    placeholder="ISBN, UPC, GTIN, etc."
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Inventory Quantity"
                    value={variant.inventoryQuantity}
                    onChange={(e) => handleVariantChange(index, 'inventoryQuantity', parseInt(e.target.value) || 0)}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Weight"
                    value={variant.weight || ''}
                    onChange={(e) =>
                      handleVariantChange(index, 'weight', e.target.value ? parseFloat(e.target.value) : null)
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Weight Unit</InputLabel>
                    <Select
                      value={variant.weightUnit}
                      label="Weight Unit"
                      onChange={(e) => handleVariantChange(index, 'weightUnit', e.target.value)}
                    >
                      <MenuItem value="kg">kg</MenuItem>
                      <MenuItem value="g">g</MenuItem>
                      <MenuItem value="lb">lb</MenuItem>
                      <MenuItem value="oz">oz</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={variant.requiresShipping}
                        onChange={(e) => handleVariantChange(index, 'requiresShipping', e.target.checked)}
                      />
                    }
                    label="Requires Shipping"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={variant.taxable}
                        onChange={(e) => handleVariantChange(index, 'taxable', e.target.checked)}
                      />
                    }
                    label="Taxable"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}
      </Box>
    </Box>
  );

  // Step 3: Images and SEO
  const renderImagesAndSEO = () => (
    <Grid container spacing={3}>
      {/* Images */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Product Images</Typography>
          <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} size="small">
            Upload New Images
            <input
              type="file"
              hidden
              multiple
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleImageFileChange}
            />
          </Button>
        </Box>

        {/* Existing Images */}
        {existingImages.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Current Images ({existingImages.length})
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {existingImages.map((image) => (
                <Grid item xs={6} sm={4} md={3} key={image.id}>
                  <Card sx={{ position: 'relative' }}>
                    <CardContent sx={{ p: 1 }}>
                      <Box sx={{ position: 'relative' }}>
                        <Avatar src={image.src} variant="rounded" sx={{ width: '100%', height: 150 }}>
                          <ImageIcon />
                        </Avatar>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveExistingImage(image.id)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'error.light' },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Position: {image.position}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {/* New Images */}
        {newImages.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              New Images to Upload ({newImages.length})
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {newImages.map((image, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Card sx={{ position: 'relative', border: '2px solid', borderColor: 'success.main' }}>
                    <CardContent sx={{ p: 1 }}>
                      <Chip label="NEW" size="small" color="success" sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }} />
                      <Box sx={{ position: 'relative' }}>
                        <Avatar src={image.preview} variant="rounded" sx={{ width: '100%', height: 150 }}>
                          <ImageIcon />
                        </Avatar>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveNewImage(index)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'background.paper',
                            '&:hover': { bgcolor: 'error.light' },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ mt: 1, display: 'block' }}>
                        {image.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(image.size / 1024).toFixed(2)} KB
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {existingImages.length === 0 && newImages.length === 0 && (
          <Alert severity="info" icon={<ImageIcon />}>
            No images added yet. Click "Upload New Images" to add product photos.
            <br />
            <Typography variant="caption" color="text.secondary">
              Supported formats: JPEG, PNG, GIF, WEBP (Max 5MB per image)
            </Typography>
          </Alert>
        )}

        {deletedImageIds.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {deletedImageIds.length} image(s) will be deleted when you save the product.
          </Alert>
        )}
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
      </Grid>

      {/* SEO */}
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Search Engine Optimization
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="SEO Title"
          value={productData.seoTitle}
          onChange={(e) => handleProductChange('seoTitle', e.target.value)}
          placeholder="Product title for search engines"
          helperText={`${productData.seoTitle.length} / 70 characters`}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="SEO Description"
          value={productData.seoDescription}
          onChange={(e) => handleProductChange('seoDescription', e.target.value)}
          placeholder="Product description for search engines"
          helperText={`${productData.seoDescription.length} / 160 characters`}
        />
      </Grid>
    </Grid>
  );

  // Step 4: Review
  const renderReview = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        Review your changes before updating the product in Shopify.
      </Alert>

      {/* Changes Summary */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Changes Summary</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {variants.filter((v) => !v.id).length}
                </Typography>
                <Typography variant="body2">New Variants</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {newImages.length}
                </Typography>
                <Typography variant="body2">New Images</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'error.light', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'error.main' }}>
                  {deletedImageIds.length}
                </Typography>
                <Typography variant="body2">Deleted Images</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'info.main' }}>
                  {variants.length}
                </Typography>
                <Typography variant="body2">Total Variants</Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Basic Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Title</Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>{productData.title}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Chip label={productData.status} size="small" color="primary" sx={{ mt: 0.5 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Vendor</Typography>
              <Typography variant="body1">{productData.vendor || 'N/A'}</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">Product Type</Typography>
              <Typography variant="body1">{productData.productType || 'N/A'}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Variants Preview</Typography>
          <Grid container spacing={1}>
            {variants.map((variant, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: variant.id ? 'divider' : 'success.main',
                    borderRadius: 1,
                    bgcolor: variant.id ? 'background.paper' : 'success.50',
                  }}
                >
                  {!variant.id && <Chip label="NEW" size="small" color="success" sx={{ mb: 1 }} />}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{variant.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    ${variant.price} | Stock: {variant.inventoryQuantity}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading product for editing...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleCancel} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Edit Shopify Product
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Update product information, variants, and images
          </Typography>
        </Box>
      </Box>

      {/* Stepper */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        {renderStepContent()}

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined">
            Back
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancel}>
              Cancel
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            ) : (
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to save these changes to the product?
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              • {variants.filter((v) => !v.id).length} new variant(s) will be added
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • {newImages.length} new image(s) will be uploaded
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • {deletedImageIds.length} image(s) will be deleted
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmSave} variant="contained" color="primary" autoFocus>
            Confirm & Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditShopifyProductPage;